import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, Wifi, WifiOff, User, Lock, Eye, Reply, Paperclip, X, FileText, Image, Download } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/^http/, "ws");

const ROLE_COLORS = {
  admin: "bg-purple-100 text-purple-700 border-purple-200",
  borrower: "bg-blue-100 text-blue-700 border-blue-200",
  lender: "bg-emerald-100 text-emerald-700 border-emerald-200",
  investor: "bg-amber-100 text-amber-700 border-amber-200",
  originations: "bg-teal-100 text-teal-700 border-teal-200",
  underwriter: "bg-indigo-100 text-indigo-700 border-indigo-200",
  processor: "bg-cyan-100 text-cyan-700 border-cyan-200",
  lender_relations: "bg-lime-100 text-lime-700 border-lime-200",
  investor_relations: "bg-orange-100 text-orange-700 border-orange-200",
};

const INTERNAL_ROLES = new Set(["admin", "originations", "underwriter", "processor", "lender_relations", "investor_relations"]);

function AttachmentPreview({ attachment, compact }) {
  if (!attachment) return null;
  const isImg = attachment.content_type?.startsWith("image/");
  if (compact) {
    return (
      <a href={attachment.storage_url} target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-lg bg-white/10 border border-white/20 text-[11px] hover:bg-white/20 transition-colors"
        data-testid="attachment-link">
        {isImg ? <Image className="h-3 w-3 shrink-0" /> : <FileText className="h-3 w-3 shrink-0" />}
        <span className="truncate max-w-[140px]">{attachment.filename}</span>
        <Download className="h-3 w-3 shrink-0 opacity-60" />
      </a>
    );
  }
  return (
    <div className="mt-2" data-testid="attachment-display">
      {isImg ? (
        <a href={attachment.storage_url} target="_blank" rel="noopener noreferrer">
          <img src={attachment.storage_url} alt={attachment.filename}
            className="max-w-[200px] max-h-[120px] rounded-lg border border-white/20 object-cover hover:opacity-90 transition-opacity" />
        </a>
      ) : (
        <a href={attachment.storage_url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 border border-white/20 hover:bg-white/20 transition-colors">
          <FileText className="h-4 w-4 shrink-0" />
          <span className="text-xs truncate max-w-[160px]">{attachment.filename}</span>
          <Download className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </a>
      )}
    </div>
  );
}

function ReplyBanner({ replyMsg, onClear }) {
  if (!replyMsg) return null;
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 mb-1 bg-emerald-50 border border-emerald-200 rounded-xl text-xs" data-testid="reply-banner">
      <Reply className="h-3 w-3 text-emerald-600 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="font-semibold text-emerald-700">{replyMsg.user_name}</span>
        <span className="text-[#888] ml-1 truncate">{replyMsg.content?.slice(0, 60)}{replyMsg.content?.length > 60 ? "..." : ""}</span>
      </div>
      <button onClick={onClear} className="p-0.5 rounded hover:bg-emerald-100" data-testid="reply-clear">
        <X className="h-3 w-3 text-[#999]" />
      </button>
    </div>
  );
}

export default function DealRoomChat({ leadId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [typingUsers, setTypingUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [messageType, setMessageType] = useState("borrower_visible");
  const [replyTo, setReplyTo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const wsRef = useRef(null);
  const endRef = useRef(null);
  const typingTimeout = useRef(null);
  const reconnectTimer = useRef(null);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem("ic_token");

  const currentUser = (() => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return { userId: payload.user_id || payload.sub, role: payload.role };
    } catch { return { userId: null, role: null }; }
  })();

  const isInternalUser = INTERNAL_ROLES.has(currentUser.role);

  const scrollToBottom = useCallback(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!leadId || !token) return;
    setLoading(true);
    axios.get(`${API}/deals/${leadId}/messages`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        setMessages(res.data.messages || []);
        setOnlineUsers(res.data.online_users || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [leadId, token]);

  useEffect(scrollToBottom, [messages, scrollToBottom]);

  useEffect(() => {
    if (!leadId || !token) return;
    const connect = () => {
      const ws = new WebSocket(`${WS_BASE}/api/ws/deals/${leadId}?token=${token}`);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        if (reconnectTimer.current) { clearTimeout(reconnectTimer.current); reconnectTimer.current = null; }
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "message") {
            setMessages(prev => prev.some(m => m.msg_id === data.msg_id) ? prev : [...prev, data]);
          } else if (data.type === "typing") {
            setTypingUsers(prev => prev.includes(data.user_name) ? prev : [...prev, data.user_name]);
            setTimeout(() => { setTypingUsers(prev => prev.filter(n => n !== data.user_name)); }, 3000);
          } else if (data.type === "presence") {
            setOnlineUsers(data.online_users || []);
          }
        } catch {}
      };
      ws.onclose = () => { setConnected(false); reconnectTimer.current = setTimeout(connect, 3000); };
      ws.onerror = () => { ws.close(); };
    };
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, [leadId, token]);

  const sendMessage = useCallback(() => {
    const text = input.trim();
    if (!text || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    const payload = {
      type: "message",
      content: text,
      message_type: isInternalUser ? messageType : "borrower_visible",
    };
    if (replyTo) payload.reply_to = replyTo.msg_id;
    if (pendingAttachment) payload.attachment = pendingAttachment;
    wsRef.current.send(JSON.stringify(payload));
    setInput("");
    setReplyTo(null);
    setPendingAttachment(null);
  }, [input, messageType, isInternalUser, replyTo, pendingAttachment]);

  const sendTyping = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    if (typingTimeout.current) return;
    wsRef.current.send(JSON.stringify({ type: "typing" }));
    typingTimeout.current = setTimeout(() => { typingTimeout.current = null; }, 2000);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/deals/${leadId}/messages/attachment`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setPendingAttachment(res.data);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const getReplyParent = (replyId) => messages.find(m => m.msg_id === replyId);

  return (
    <div className="flex flex-col h-full min-h-[400px]" data-testid="deal-room-chat">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {connected ? (
            <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium" data-testid="chat-status-connected">
              <Wifi className="h-3 w-3" /> Live
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-xs text-[#999] font-medium" data-testid="chat-status-disconnected">
              <WifiOff className="h-3 w-3" /> Connecting...
            </span>
          )}
        </div>
        {onlineUsers.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-[#888]" data-testid="online-users-count">
            <div className="flex -space-x-1">
              {onlineUsers.slice(0, 3).map((_, i) => (
                <div key={i} className="h-4 w-4 rounded-full bg-emerald-100 border border-white flex items-center justify-center">
                  <User className="h-2.5 w-2.5 text-emerald-600" />
                </div>
              ))}
            </div>
            {onlineUsers.length} online
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-2.5 px-1 min-h-0" data-testid="chat-messages-list">
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 text-sm text-[#999]">No messages yet. Start the conversation about this deal.</div>
        ) : (
          messages.map((msg) => {
            const isMine = msg.user_id === currentUser.userId;
            const roleClass = ROLE_COLORS[msg.user_role] || "bg-slate-100 text-slate-600 border-slate-200";
            const isInternal = msg.message_type === "internal";
            const parentMsg = msg.reply_to ? getReplyParent(msg.reply_to) : null;
            return (
              <motion.div key={msg.msg_id || msg.created_at} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className={`flex ${isMine ? "justify-end" : "justify-start"} group`}>
                <div className={`max-w-[80%] ${isMine ? "text-right" : "text-left"}`}>
                  {!isMine && (
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-xs font-medium text-[#333]">{msg.user_name}</span>
                      <span className={`px-1.5 py-0 rounded text-[9px] font-bold uppercase border ${roleClass}`}>{msg.user_role}</span>
                      {isInternal && (
                        <span className="flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase bg-red-50 text-red-500 border border-red-200" data-testid="internal-badge">
                          <Lock className="h-2.5 w-2.5" /> Internal
                        </span>
                      )}
                    </div>
                  )}
                  {/* Reply context */}
                  {parentMsg && (
                    <div className={`flex items-center gap-1.5 mb-1 px-2.5 py-1 rounded-lg border text-[10px] ${isMine ? "bg-white/50 border-[#e8e0d0] ml-auto" : "bg-[#f5f2eb] border-[#e8e0d0]"}`}
                      data-testid="reply-context">
                      <Reply className="h-2.5 w-2.5 text-[#999] shrink-0" />
                      <span className="font-semibold text-[#666]">{parentMsg.user_name}:</span>
                      <span className="text-[#999] truncate">{parentMsg.content?.slice(0, 40)}{parentMsg.content?.length > 40 ? "..." : ""}</span>
                    </div>
                  )}
                  <div className={`inline-block rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                    isInternal
                      ? (isMine ? "bg-red-700/90 text-white rounded-br-md border border-red-600" : "bg-red-50 border border-red-200 text-[#333] rounded-bl-md")
                      : (isMine ? "bg-emerald-700 text-white rounded-br-md" : "bg-[#faf7f2] border border-[#e8e0d0] text-[#333] rounded-bl-md")
                  }`}>
                    {msg.content}
                    {msg.attachment && <AttachmentPreview attachment={msg.attachment} compact={isMine} />}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 px-1">
                    {isMine && isInternal && (
                      <span className="flex items-center gap-0.5 text-[9px] text-red-400 font-medium" data-testid="internal-indicator">
                        <Lock className="h-2 w-2" /> Internal
                      </span>
                    )}
                    <span className="text-[10px] text-[#bbb]">
                      {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                    <button onClick={() => setReplyTo(msg)} data-testid={`reply-btn-${msg.msg_id}`}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-[#e8e0d0] text-[#999] hover:text-emerald-700">
                      <Reply className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
        <AnimatePresence>
          {typingUsers.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-xs text-[#999] italic">
              {typingUsers.join(", ")} {typingUsers.length === 1 ? "is" : "are"} typing...
            </motion.div>
          )}
        </AnimatePresence>
        <div ref={endRef} />
      </div>

      {/* Reply + Attachment banners */}
      <div className="mt-2">
        <ReplyBanner replyMsg={replyTo} onClear={() => setReplyTo(null)} />
        {pendingAttachment && (
          <div className="flex items-center gap-2 px-3 py-1.5 mb-1 bg-blue-50 border border-blue-200 rounded-xl text-xs" data-testid="pending-attachment">
            <Paperclip className="h-3 w-3 text-blue-600 shrink-0" />
            <span className="flex-1 truncate text-blue-700 font-medium">{pendingAttachment.filename}</span>
            <button onClick={() => setPendingAttachment(null)} className="p-0.5 rounded hover:bg-blue-100" data-testid="attachment-clear">
              <X className="h-3 w-3 text-[#999]" />
            </button>
          </div>
        )}
      </div>

      {/* Message type toggle */}
      {isInternalUser && (
        <div className="flex items-center gap-2" data-testid="message-type-toggle">
          <button onClick={() => setMessageType("borrower_visible")} data-testid="msg-type-visible"
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors ${
              messageType === "borrower_visible" ? "bg-emerald-100 text-emerald-700 border border-emerald-300" : "bg-[#faf7f2] text-[#888] border border-[#e8e0d0] hover:border-emerald-200"
            }`}>
            <Eye className="h-3 w-3" /> Visible to All
          </button>
          <button onClick={() => setMessageType("internal")} data-testid="msg-type-internal"
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors ${
              messageType === "internal" ? "bg-red-100 text-red-600 border border-red-300" : "bg-[#faf7f2] text-[#888] border border-[#e8e0d0] hover:border-red-200"
            }`}>
            <Lock className="h-3 w-3" /> Internal Only
          </button>
        </div>
      )}

      {/* Input area */}
      <div className="mt-2 flex items-center gap-2 pt-3 border-t border-[#e8e0d0]">
        <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileSelect} data-testid="file-input" />
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="attach-btn"
          className="p-2.5 rounded-xl border border-[#e8e0d0] text-[#888] hover:text-emerald-700 hover:border-emerald-300 transition-colors disabled:opacity-40">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </button>
        <input value={input} onChange={e => { setInput(e.target.value); sendTyping(); }} onKeyDown={handleKeyDown}
          placeholder={connected ? (messageType === "internal" ? "Internal message (staff only)..." : "Type a message...") : "Connecting..."}
          disabled={!connected} data-testid="deal-chat-input"
          className={`flex-1 text-sm px-3 py-2.5 rounded-xl border bg-[#faf7f2] text-[#0A0A0A] placeholder:text-[#999] focus:outline-none focus:ring-1 transition-colors disabled:opacity-50 ${
            messageType === "internal" ? "border-red-300 focus:border-red-400 focus:ring-red-400/30" : "border-[#e8e0d0] focus:border-emerald-400 focus:ring-emerald-400/30"
          }`}
        />
        <button onClick={sendMessage} disabled={!connected || !input.trim()} data-testid="deal-chat-send"
          className={`p-2.5 rounded-xl text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0 ${
            messageType === "internal" ? "bg-red-600 hover:bg-red-500" : "bg-emerald-700 hover:bg-emerald-600"
          }`}>
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
